import type { HTMLAttributes, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const TOOFI_SHELL_SURFACE_BASE_CLASS =
  "overflow-hidden bg-[linear-gradient(180deg,rgba(255,255,255,0.95),rgba(248,250,252,0.9))] backdrop-blur-[20px] transition-[inset,width,height,border-radius,transform,opacity,background,box-shadow] duration-300 ease-out";

export const TOOFI_SHELL_SURFACE_DOCKED_CLASS =
  "rounded-[2.25rem] border border-[var(--athr-border-soft)] shadow-[0_28px_80px_rgba(15,23,42,0.12)]";

export const TOOFI_SHELL_SURFACE_FULLSCREEN_CLASS =
  "inset-0 h-screen w-screen rounded-none border-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.94))] shadow-none";

export const TOOFI_FLOATING_CONTROL_BUTTON_CLASS =
  "h-10 w-10 rounded-full border border-[var(--athr-border-soft)] bg-white/88 text-slate-500 shadow-[0_14px_32px_rgba(148,163,184,0.14)] backdrop-blur-xl transition-all duration-200 hover:-translate-y-0.5 hover:bg-white hover:text-slate-900 active:scale-95";

type ToofiShellSurfaceProps = {
  as?: "div" | "section";
  fullScreen?: boolean;
  dockedClassName?: string;
  fullScreenClassName?: string;
  children: ReactNode;
} & HTMLAttributes<HTMLDivElement>;

export function ToofiShellSurface({
  as = "section",
  fullScreen = false,
  className,
  dockedClassName,
  fullScreenClassName,
  children,
  ...rest
}: ToofiShellSurfaceProps) {
  const Component = as;

  return (
    <Component
      {...rest}
      className={cn(
        TOOFI_SHELL_SURFACE_BASE_CLASS,
        fullScreen
          ? cn(TOOFI_SHELL_SURFACE_FULLSCREEN_CLASS, fullScreenClassName)
          : cn(TOOFI_SHELL_SURFACE_DOCKED_CLASS, dockedClassName),
        className,
      )}
    >
      {children}
    </Component>
  );
}

type ToofiFloatingIconButtonProps = {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  title?: string;
  className?: string;
};

export function ToofiFloatingIconButton({
  icon,
  label,
  onClick,
  title,
  className,
}: ToofiFloatingIconButtonProps) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label}
      title={title ?? label}
      className={cn(TOOFI_FLOATING_CONTROL_BUTTON_CLASS, className)}
      onClick={onClick}
    >
      {icon}
    </Button>
  );
}
